import { useEffect, useState } from 'react';
import { listVerdicts, type VerdictRow } from '../api';
import { FamilyFilter } from '../lib/family';
import { Pager, ScanNote, useCursorList } from '../lib/paging';
import { replace } from '../lib/route';
import { Empty, Pill, Result, Toolbar, when } from '../lib/ui';

/**
 * The safety gate's decisions: 162 allowed, 65 refused, all on one policy
 * version.
 *
 * Three things about this table decide how the screen behaves, and all three are
 * properties of the data rather than preferences.
 *
 * **It is a cache, not an audit log.** TTL is ENABLED — 7 days for allows, 30 for
 * denies — so an absent row means "not asked recently", never "never asked". A
 * screen that let an operator conclude "the gate has never refused this" would be
 * inviting a wrong answer to a safety question.
 *
 * **The partition key is the parent's own words.** `safetyKey` is
 * `<policyVersion>#<normalised request text>`, and live rows have been verified to
 * contain a child's first name and a home address, because parents type what they
 * mean and the gate cannot strip names it has not identified yet. So the API
 * returns a stable fingerprint by default, and revealing is a separate act that
 * logs the operator's name server-side. There is deliberately no "reveal all"
 * button here — reveal is a whole-page mode you have to turn on, so it is a
 * decision rather than a stray click.
 *
 * **There is no familyId, and there never will be.** The cache is global by
 * design. "Which family asked for the thing we refused" is not answerable from
 * this table, and the API returns a 400 rather than silently ignoring a family
 * filter — because ignoring it would render every denial in the system under one
 * household's name.
 */
export function Verdicts({ initialAllowed }: { initialAllowed?: string }) {
  const [allowed, setAllowed] = useState(initialAllowed ?? 'false');
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    replace({ name: 'verdicts', ...(allowed ? { allowed } : {}) });
  }, [allowed]);

  const list = useCursorList<VerdictRow>(
    (cursor) =>
      listVerdicts({
        ...(allowed ? { allowed } : {}),
        ...(reveal ? { reveal: '1' } : {}),
        ...(cursor ? { next: cursor } : {}),
      }),
    [allowed, reveal],
  );

  return (
    <>
      <div className="notice notice-warn">
        <b>This is a 7/30-day cache, not an audit trail.</b>
        <span className="muted small">
          Allows expire after 7 days, denials after 30. A request that is not here
          was not necessarily never made — it may simply have aged out. And there
          is no familyId on a verdict: the cache is global, so no row can be
          attributed to a household.
        </span>
      </div>

      <Toolbar>
        <span className="group">
          <label htmlFor="verdict-decision">Decision</label>
          <select
            id="verdict-decision"
            value={allowed}
            onChange={(e) => setAllowed(e.target.value)}
          >
            <option value="false">refused</option>
            <option value="true">allowed</option>
            <option value="">both</option>
          </select>
        </span>
        {/* Rendered disabled rather than omitted: an absent control reads as an
            oversight, and this one is a property of the table. */}
        <FamilyFilter
          value=""
          onChange={() => undefined}
          disabledReason="showling-verdicts records no familyId — the cache is global by design"
        />
        <span className="spacer" />
        <button className={reveal ? 'destructive' : ''} onClick={() => setReveal(!reveal)}>
          {reveal ? 'hide the request text' : 'reveal the request text'}
        </button>
      </Toolbar>

      {reveal && (
        <div className="notice notice-bad">
          <b>Showing what parents typed, in full.</b>
          <span className="muted small">
            This is logged server-side with your name. Live rows in this table
            include a child's first name and a home address — the gate stores the
            raw text as its partition key because it cannot redact names it has not
            identified yet. Turn this back off when you are done.
          </span>
        </div>
      )}

      <Result of={list.result} what="verdicts" retry={list.reload}>
        {(page) => (
          <>
            <Toolbar>
              <Pager list={list} what="verdicts" />
            </Toolbar>
            {!page.rows.length ? (
              <Empty what="verdicts" filtered={allowed !== ''} />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Decided</th>
                    <th>Decision</th>
                    <th>{reveal ? 'What was asked for' : 'Request'}</th>
                    <th>Reason</th>
                    <th>Suggested instead</th>
                    <th>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {page.rows.map((row) => (
                    <tr
                      key={`${row.policyVersion}-${row.textFingerprint}`}
                      className={row.allowed === false ? 'flagged' : ''}
                    >
                      <td className="muted nowrap">{when(row.at)}</td>
                      <td>
                        {row.allowed ? (
                          <Pill tone="on">allowed</Pill>
                        ) : (
                          <Pill tone="bad">refused</Pill>
                        )}
                        {row.verdict === 'unparseable' && (
                          <div className="warn small" title="the stored verdict JSON would not parse — shown with the fields it does have rather than dropped">
                            unparseable
                          </div>
                        )}
                      </td>
                      <td>
                        {row.text !== undefined ? (
                          <span>{row.text}</span>
                        ) : (
                          <span className="muted small">
                            <code>{row.textFingerprint}</code> · {row.textLength} chars
                          </span>
                        )}
                      </td>
                      {/* Never redacted, and worth knowing why: the reason IS the
                          decision, and a safety trail without it audits nothing.
                          It can still quote the parent's words back. */}
                      <td className="small">{row.reason}</td>
                      <td className="muted small">{row.suggestion}</td>
                      <td className="muted nowrap">
                        {row.expired ? (
                          <Pill tone="warn" title="past its TTL — the sweep has not reached it yet, and the gate re-checks expiry on read">
                            expired
                          </Pill>
                        ) : (
                          when(row.ttlAt)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <ScanNote listing={page} />
            <p className="muted small">
              Read-only, permanently. A write here would change what the gate
              allows for every family for up to 30 days, with no ledger row
              anywhere and no way to tell an operator's edit from the model's own
              answer.
            </p>
          </>
        )}
      </Result>
    </>
  );
}
